import { useState } from "react";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

/** One-click download of a campaign performance summary as PDF or Excel. */
export function ReportDownload({ campaignId, platform, testId = "report-download" }) {
  const [busy, setBusy] = useState("");

  const download = async (fmt) => {
    if (busy) return;
    setBusy(fmt);
    try {
      const response = await api.get(`/reports/campaign/${campaignId}/export`, {
        params: { fmt, platform: platform || undefined },
        responseType: "blob",
      });
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = match ? match[1] : `campaign-performance.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(fmt === "pdf" ? "Performance PDF downloaded" : "Excel workbook downloaded");
    } catch (e) {
      toast.error(apiError(e, "The report could not be generated. Please retry."));
    } finally {
      setBusy("");
    }
  };

  const base =
    "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60";

  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      <button
        type="button"
        disabled={!campaignId || Boolean(busy)}
        onClick={() => download("pdf")}
        data-testid={`${testId}-pdf`}
        className={`${base} border-[#F2811D] bg-[#F2811D] text-white hover:bg-[#d97316]`}
      >
        {busy === "pdf" ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <FileText className="h-4 w-4" />
        )}
        Download PDF
      </button>
      <button
        type="button"
        disabled={!campaignId || Boolean(busy)}
        onClick={() => download("xlsx")}
        data-testid={`${testId}-xlsx`}
        className={`${base} border-[#D9D2C6] bg-white text-[#111111] hover:border-[#111111]`}
      >
        {busy === "xlsx" ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <FileSpreadsheet className="h-4 w-4" />
        )}
        Download Excel
      </button>
    </div>
  );
}
